import React from 'react';
import { motion } from 'framer-motion';

const EventDetails = () => {
    return (
        <section className="py-32 bg-wedding-blush">
            <div className="container mx-auto px-4 max-w-5xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-20"
                >
                    <h2 className="text-4xl md:text-5xl font-serif text-wedding-accent mb-4">
                        Celebration Details
                    </h2>
                    <div className="w-16 h-[1px] bg-wedding-primary mx-auto"></div>
                </motion.div>

                <motion.div
                    className="bg-white max-w-2xl mx-auto"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="p-12 md:p-16 text-center space-y-8">
                        {/* Section Header with Heart Flourish */}
                        <div className="text-center mb-12">
                            {/* Heart Flourish Divider */}
                            <div className="flex justify-center mb-6">
                                <svg
                                    width="200"
                                    height="80"
                                    viewBox="0 0 200 80"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="text-wedding-accent"
                                >
                                    {/* Left Flourish */}
                                    <path
                                        d="M10 40 Q 40 35, 70 40"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M70 40 Q 75 45, 80 42"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                    />

                                    {/* Heart */}
                                    <path
                                        d="M100 35 C 95 25, 80 25, 80 35 C 80 42, 100 55, 100 55 C 100 55, 120 42, 120 35 C 120 25, 105 25, 100 35"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />

                                    {/* Right Flourish */}
                                    <path
                                        d="M120 42 Q 125 45, 130 40"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M130 40 Q 160 35, 190 40"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                    />

                                    {/* Decorative Loops */}
                                    <path
                                        d="M75 45 Q 70 55, 75 50"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        fill="none"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M125 45 Q 130 55, 125 50"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        fill="none"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </div>

                            {/* Heading */}
                            <h2 className="text-4xl md:text-6xl font-serif text-wedding-accent mb-4">
                                Wedding Ceremony
                            </h2>

                            <p className="text-wedding-charcoal/60 text-lg max-w-2xl mx-auto">
                                Join us as we exchange vows and begin our forever
                            </p>
                        </div>

                        {/* TIME SECTION - PROFESSIONAL DESIGN */}
                        <div className="border-t border-b border-wedding-accent/20 py-6 my-8">
                            <div className="flex items-center justify-center gap-3">
                                {/* Clock Icon */}
                                <svg 
                                    className="w-6 h-6 text-wedding-accent" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                                    />
                                </svg>
                                
                                {/* Time Text */}
                                <span className="text-4xl md:text-5xl font-serif font-bold text-wedding-accent">
                                    8:00 PM
                                </span>
                            </div>
                        </div>

                        {/* Venue Details */}
                        <div className="space-y-4 text-gray-700">
                            <h3 className="text-3xl md:text-4xl font-serif font-bold text-wedding-charcoal">
                                The Aurelia Banquet
                            </h3>
                            <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                                CBD, Ground, Plot A, Vishwas Nagar Extension,<br />
                                Karkardooma, Shahdara, Delhi - 110032
                            </p>
                        </div>

                        {/* Get Directions Button */}
                        <div className="pt-6">
                            <a
                                href="https://maps.app.goo.gl/hfM85ZPJVibkZesf8"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm uppercase tracking-widest font-medium text-wedding-accent hover:text-wedding-primary transition-colors duration-300 border-b border-wedding-accent hover:border-wedding-primary pb-1"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Get Directions →
                            </a>
                        </div>
                    </div>
                </motion.div>

                {/* Dress Code */}
                <motion.div
                    className="mt-12 text-center"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                >
                    <p className="text-sm text-gray-600 uppercase tracking-widest">
                        Dress Code: Traditional & Formal Attire
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default EventDetails;
